import { AfterViewInit, Component, ElementRef, OnDestroy } from "@angular/core";
import {
  IExternalApiAvailableSkusResponse,
  IExternalApiCallbackPayload,
  IExternalApiCreateOrderRequest,
  IExternalApiCreateOrderResponse,
  IExternalApiErrorResponse,
  IExternalApiGetOrderResponse
} from "../../../../../../common/common-interfaces/external-api";

export interface IApiDocField {
  name: string,
  type: string,
  required?: boolean,
  description: string,
  /** Id of a section in Data types that describes the type */
  typeRef?: string
}

export interface IApiDocError {
  status: number,
  response: string,
  description: string
}

export interface IApiDocEndpoint {
  id: string,
  title: string,
  method: 'GET' | 'POST',
  path: string,
  /** Query string used in the curl example, e.g. ?orderNumber=ACE00128 */
  exampleQuery?: string,
  description: string[],
  queryParams?: IApiDocField[],
  requestFields?: IApiDocField[],
  responseFields: IApiDocField[],
  requestExample?: any,
  responseExample: any,
  errors: IApiDocError[]
}

export interface IApiDocType {
  id: string,
  name: string,
  description: string,
  fields: IApiDocField[]
}

export interface IApiDocNavItem {
  id: string,
  label: string,
  method?: string
}

const authErrors: IApiDocError[] = [
  { status: 401, response: 'Empty', description: 'The Access-Code header is missing or wrong.' },
  { status: 200, response: 'Empty', description: 'There is no store with the store ID.' },
];

const availableSkusResponseExample: IExternalApiAvailableSkusResponse = {
  POSTER: [
    { sku: 'Poster_30x40_obestruket', size: '30x40' },
    { sku: 'Poster_50x70_obestruket', size: '50x70' },
    { sku: 'Poster_50x70_bestruket', size: '50x70' }
  ],
  CANVAS: [
    { sku: 'Canvas_50x70', size: '50x70' }
  ],
  STOCK_ITEM: [
    { sku: 'HOOK', size: null },
    { sku: 'FRAME_50x70_BLACK', size: null }
  ],
  OTHER: [
    { sku: 'STUDENT-SIGN', size: 'STD_STUDENT_50x70' },
    { sku: 'MUG-WHITE', size: 'STD_MUG_11' },
    { sku: 'MUG-BLACK', size: 'STD_MUG_11' }
  ]
};

const createOrderRequestExample: IExternalApiCreateOrderRequest = {
  externalId: 'SHOP-10234',
  currency: 'SEK',
  sandbox: true,
  shippingInfo: {
    firstName: 'Anna',
    lastName: 'Andersson',
    email: 'anna@example.com',
    phone: '+46701234567',
    address1: 'Storgatan 1',
    city: 'Stockholm',
    zipCode: '11122',
    country: 'SE'
  },
  items: [
    {
      name: 'Sunset Beach 50x70',
      processedSku: 'Poster_50x70_obestruket',
      type: 'POSTER',
      quantity: 2,
      printFileUrl: 'https://example.com/print-files/sunset-beach-50x70.pdf',
      price: { withVat: 399, withoutVat: 319.2, vat: 79.8, vatPercentage: 25 }
    },
    {
      name: 'Northern Lights canvas 50x70',
      processedSku: 'Canvas_50x70',
      type: 'CANVAS',
      quantity: 1,
      originalFileUrl: 'https://example.com/images/northern-lights.jpg',
      price: { withVat: 899, withoutVat: 719.2, vat: 179.8, vatPercentage: 25 }
    },
    {
      name: 'Black frame 50x70',
      processedSku: 'FRAME_50x70_BLACK',
      type: 'STOCK_ITEM',
      quantity: 1,
      price: { withVat: 249, withoutVat: 199.2, vat: 49.8, vatPercentage: 25 }
    }
  ],
  callbackUrl: 'https://example.com/callbacks/ace-order',
  sendShippingDispatchEmail: false
};

const createOrderResponseExample: IExternalApiCreateOrderResponse = {
  order: {
    id: '6650c3a9a8e4b51d2c9f0b42',
    orderNumber: 'ACE00128',
    status: 'PENDING',
    externalId: 'SHOP-10234',
    shippingInfo: createOrderRequestExample.shippingInfo,
    items: [
      {
        identifier: '0b9d7a52-5d1e-4c64-9d0e-4f4c1c3a8e21',
        name: 'Sunset Beach 50x70',
        sku: 'Poster_50x70_obestruket',
        quantity: 2,
        printFileUrl: 'https://example.com/print-files/sunset-beach-50x70.pdf',
        status: 'READY'
      },
      {
        identifier: '3a7e9c14-2f6b-4d8a-b1e5-7c0d9f2a4b63',
        name: 'Northern Lights canvas 50x70',
        sku: 'Canvas_50x70',
        quantity: 1,
        status: 'PENDING_POSTER'
      },
      {
        identifier: '6f1c2e0a-8b7d-4a1f-9c3e-2d5b7a9e1c40',
        name: 'Black frame 50x70',
        sku: 'FRAME_50x70_BLACK',
        quantity: 1,
        status: 'READY'
      }
    ]
  }
};

const errorExample: IExternalApiErrorResponse = {
  error: 'Failed to create order'
};

const callbackExample: IExternalApiCallbackPayload = {
  name: 'ORDER_DISPATCHED',
  order: {
    ...createOrderResponseExample.order,
    status: 'SHIPPED',
    shippingDispatches: [
      {
        fulfillmentPartner: 'EXAKTA',
        trackingNumber: '00370712345678901234',
        carrier: 'PostNord',
        dispatchDate: new Date('2026-10-14T09:12:00.000Z'),
        trackingUrl: 'https://tracking.postnord.com/tracking.html?id=00370712345678901234',
        dispatchedItems: [
          { name: 'Sunset Beach 50x70', identifier: '0b9d7a52-5d1e-4c64-9d0e-4f4c1c3a8e21', quantity: 2 }
        ]
      }
    ]
  }
};

const getOrderResponseExample: IExternalApiGetOrderResponse = {
  order: callbackExample.order
};

@Component({
  selector: "api-documentation",
  templateUrl: "./api-documentation.component.html",
  styleUrls: ["./api-documentation.component.scss"],
  standalone: false
})
export class ApiDocumentationComponent implements AfterViewInit, OnDestroy {

  readonly baseUrl = `${window.location.origin}/api/stores/public/{storeId}/third-party/external`;

  activeSectionId = 'introduction';
  copiedKey?: string;

  private observer?: IntersectionObserver;
  private copiedTimeout?: any;

  readonly endpoints: IApiDocEndpoint[] = [
    {
      id: 'available-skus',
      title: 'List available SKUs',
      method: 'GET',
      path: '/skus/available',
      description: [
        'Lists the products that can be ordered, grouped by type. Use an item\'s sku as processedSku and its group as type when you create an order.'
      ],
      responseFields: [
        { name: 'POSTER', type: 'SkuInfo[]', typeRef: 'type-sku-info', required: true, description: 'Posters. The SKU ends with the paper type: obestruket (uncoated) or bestruket (coated).' },
        { name: 'CANVAS', type: 'SkuInfo[]', typeRef: 'type-sku-info', required: true, description: 'Canvas prints.' },
        { name: 'STOCK_ITEM', type: 'SkuInfo[]', typeRef: 'type-sku-info', required: true, description: 'Products that need no print file, such as frames and hooks.' },
        { name: 'OTHER', type: 'SkuInfo[]', typeRef: 'type-sku-info', required: true, description: 'Student signs and mugs. Mugs come in WHITE, RED, PINK, GREEN, BLUE, BLACK and YELLOW.' },
      ],
      responseExample: availableSkusResponseExample,
      errors: [
        ...authErrors
      ]
    },
    {
      id: 'create-order',
      title: 'Create an order',
      method: 'POST',
      path: '/orders/create',
      description: [
        'Creates an order. It is sent to print as soon as every item has a print file. Give each poster and canvas item either a printFileUrl with a print-ready file, or an originalFileUrl with an image to generate the print file from. See Print files.',
        'Apart from printFileUrl and originalFileUrl, the request is not validated, so check the order in the response.'
      ],
      requestFields: [
        { name: 'externalId', type: 'string', description: 'The order\'s ID in your system. Returned in the order and in callbacks. Not checked for duplicates.' },
        { name: 'currency', type: 'string', required: true, description: 'Three letter currency code of the item prices, e.g. SEK.' },
        { name: 'shippingInfo', type: 'ContactInfo', typeRef: 'type-contact-info', required: true, description: 'Recipient of the order.' },
        { name: 'items', type: 'OrderItemRequest[]', typeRef: 'type-order-item-request', required: true, description: 'The products to print and ship.' },
        { name: 'sandbox', type: 'boolean', description: 'Create a test order that is never printed. See Sandbox orders.' },
        { name: 'callbackUrl', type: 'string', description: 'URL that receives a POST when the order is printed, dispatched, delivered or canceled. See Callbacks.' },
        { name: 'sendShippingDispatchEmail', type: 'boolean', description: 'Email the customer a shipping confirmation when the order is dispatched.' },
      ],
      responseFields: [
        { name: 'order', type: 'Order', typeRef: 'type-order', required: true, description: 'The created order.' },
      ],
      requestExample: createOrderRequestExample,
      responseExample: createOrderResponseExample,
      errors: [
        { status: 400, response: '{ "error": "…" }', description: 'A printFileUrl of a POSTER, CANVAS or OTHER item is not a PDF file ending with .pdf. The message says which item.' },
        { status: 400, response: '{ "error": "…" }', description: 'The processedSku of an OTHER item is not listed under OTHER in GET /skus/available. The message says which item.' },
        { status: 400, response: '{ "error": "…" }', description: 'An originalFileUrl is not a direct https link to a JPG, PNG, WebP or TIFF file, or is given for an item that is not POSTER, CANVAS or OTHER. The message says which.' },
        { status: 500, response: '{ "error": "Failed to create order" }', description: 'The order could not be created.' },
        ...authErrors
      ]
    },
    {
      id: 'get-order',
      title: 'Get an order',
      method: 'GET',
      path: '/order',
      exampleQuery: '?externalId=SHOP-10234',
      description: [
        'Gets an order of the store by its order number or its externalId, e.g. to check its status and tracking information.',
        'externalId is not unique, so if several orders have the same one, the most recent is returned. If both parameters are given, the order must match both.'
      ],
      queryParams: [
        { name: 'orderNumber', type: 'string', description: 'The orderNumber returned when the order was created. Required unless externalId is given.' },
        { name: 'externalId', type: 'string', description: 'The externalId the order was created with. Required unless orderNumber is given.' },
      ],
      responseFields: [
        { name: 'order', type: 'Order', typeRef: 'type-order', required: true, description: 'The order.' },
      ],
      responseExample: getOrderResponseExample,
      errors: [
        { status: 400, response: '{ "error": "…" }', description: 'Neither orderNumber nor externalId is given.' },
        { status: 404, response: '{ "error": "Order not found" }', description: 'The store has no order matching the parameters.' },
        { status: 500, response: '{ "error": "Failed to get order" }', description: 'The order could not be read.' },
        ...authErrors
      ]
    }
  ];

  readonly callbackFields: IApiDocField[] = [
    { name: 'name', type: 'string', required: true, description: 'What happened: ORDER_PRINTED, ORDER_DISPATCHED, ORDER_DELIVERED or ORDER_CANCELED.' },
    { name: 'order', type: 'Order', typeRef: 'type-order', required: true, description: 'The order, including shippingDispatches once it has been dispatched.' },
  ];

  readonly callbackNames: { name: string, description: string }[] = [
    { name: 'ORDER_PRINTED', description: 'The print partner has processed the order.' },
    { name: 'ORDER_DISPATCHED', description: 'The order has been handed to the carrier. order.shippingDispatches holds the tracking information.' },
    { name: 'ORDER_DELIVERED', description: 'Every parcel of the order has been delivered.' },
    { name: 'ORDER_CANCELED', description: 'The order has been canceled in the gateway.' },
  ];

  readonly types: IApiDocType[] = [
    {
      id: 'type-contact-info',
      name: 'ContactInfo',
      description: 'The recipient of an order.',
      fields: [
        { name: 'firstName', type: 'string', required: true, description: '' },
        { name: 'lastName', type: 'string', required: true, description: '' },
        { name: 'email', type: 'string', required: true, description: '' },
        { name: 'phone', type: 'string', required: true, description: 'Carriers use it for delivery notices.' },
        { name: 'address1', type: 'string', required: true, description: '' },
        { name: 'address2', type: 'string', description: '' },
        { name: 'city', type: 'string', required: true, description: '' },
        { name: 'zipCode', type: 'string', required: true, description: '' },
        { name: 'company', type: 'string', description: '' },
        { name: 'country', type: 'string', required: true, description: 'Two letter country code, e.g. SE.' },
      ]
    },
    {
      id: 'type-order-item-request',
      name: 'OrderItemRequest',
      description: 'An item in a create order request.',
      fields: [
        { name: 'name', type: 'string', required: true, description: 'Display name of the item.' },
        { name: 'processedSku', type: 'string', required: true, description: 'A sku from List available SKUs, e.g. Poster_50x70_obestruket.' },
        { name: 'type', type: 'string', required: true, description: 'The group the SKU is listed in: POSTER, CANVAS, STOCK_ITEM or OTHER.' },
        { name: 'quantity', type: 'number', required: true, description: 'A positive integer.' },
        { name: 'price', type: 'Price', typeRef: 'type-price', required: true, description: 'Unit price the customer paid. The prices make up the order total.' },
        { name: 'printFileUrl', type: 'string', description: 'Public URL of a print-ready PDF file in the size of the SKU. The path must end with .pdf, or the request fails with 400. Takes precedence over originalFileUrl. Not used for stock items.' },
        { name: 'originalFileUrl', type: 'string', description: 'Public https URL of an image to print, ending with .jpg, .jpeg, .png, .webp, .tif or .tiff. The print file is generated from it, cropped from the center to the aspect ratio of the size. Only for POSTER, CANVAS and OTHER items. See Print files.' },
      ]
    },
    {
      id: 'type-price',
      name: 'Price',
      description: 'An amount in the order\'s currency.',
      fields: [
        { name: 'withVat', type: 'number', required: true, description: '' },
        { name: 'withoutVat', type: 'number', required: true, description: '' },
        { name: 'vat', type: 'number', required: true, description: '' },
        { name: 'vatPercentage', type: 'number', required: true, description: 'E.g. 25.' },
      ]
    },
    {
      id: 'type-sku-info',
      name: 'SkuInfo',
      description: 'A product that can be ordered.',
      fields: [
        { name: 'sku', type: 'string', required: true, description: 'Use as processedSku.' },
        { name: 'size', type: 'string | null', required: true, description: 'Width x height in cm, e.g. 50x70. null for stock items.' },
      ]
    },
    {
      id: 'type-order',
      name: 'Order',
      description: 'An order as the API returns it.',
      fields: [
        { name: 'id', type: 'string', required: true, description: 'ID of the order in the gateway.' },
        { name: 'orderNumber', type: 'string', required: true, description: 'Order number shown in the gateway.' },
        { name: 'status', type: 'OrderStatus', typeRef: 'order-statuses', required: true, description: 'Current status of the order.' },
        { name: 'externalId', type: 'string', description: 'The externalId the order was created with.' },
        { name: 'shippingInfo', type: 'ContactInfo', typeRef: 'type-contact-info', required: true, description: '' },
        { name: 'items', type: 'OrderItem[]', typeRef: 'type-order-item', required: true, description: '' },
        { name: 'shippingDispatches', type: 'ShippingDispatch[]', typeRef: 'type-shipping-dispatch', description: 'Shipments of the order, once it has been dispatched.' },
      ]
    },
    {
      id: 'type-order-item',
      name: 'OrderItem',
      description: 'An item of an order as the API returns it.',
      fields: [
        { name: 'identifier', type: 'string', required: true, description: 'ID of the item in the order.' },
        { name: 'name', type: 'string', required: true, description: '' },
        { name: 'sku', type: 'string', required: true, description: 'The processedSku of the item.' },
        { name: 'quantity', type: 'number', required: true, description: '' },
        { name: 'printFileUrl', type: 'string', description: 'The item\'s print file, once it has one.' },
        { name: 'status', type: 'string', required: true, description: 'READY when the item can be printed, PENDING_POSTER while its print file is generated from originalFileUrl, AWAITING_PRINT_FILE while it waits for a print file to be added in the gateway.' },
      ]
    },
    {
      id: 'type-shipping-dispatch',
      name: 'ShippingDispatch',
      description: 'A shipment of an order. An order can be shipped in several parcels.',
      fields: [
        { name: 'fulfillmentPartner', type: 'string', required: true, description: 'The print partner that shipped the parcel.' },
        { name: 'trackingNumber', type: 'string', required: true, description: '' },
        { name: 'carrier', type: 'string', required: true, description: '' },
        { name: 'trackingUrl', type: 'string', required: true, description: '' },
        { name: 'dispatchDate', type: 'string', required: true, description: 'ISO 8601 date.' },
        { name: 'dispatchedItems', type: 'object[]', description: 'The items in the parcel, with name, identifier and quantity.' },
        { name: 'delivered', type: 'boolean', description: '' },
        { name: 'deliveredDate', type: 'string', description: 'ISO 8601 date.' },
        { name: 'returned', type: 'boolean', description: '' },
        { name: 'returnedDate', type: 'string', description: 'ISO 8601 date.' },
      ]
    },
  ];

  readonly orderStatuses: { status: string, description: string }[] = [
    { status: 'PENDING', description: 'Waiting for print files or a manual check.' },
    { status: 'READY', description: 'Every item has a print file. The order will be sent to print.' },
    { status: 'QUEUED_FOR_PRINT', description: 'Sent to the print partner.' },
    { status: 'PRINTED', description: 'Printed and waiting for shipment.' },
    { status: 'SHIPPED', description: 'Handed to the carrier. See shippingDispatches.' },
    { status: 'DELIVERED', description: 'Delivered to the recipient.' },
    { status: 'RETURNED', description: 'Returned to the sender.' },
    { status: 'CANCELLED', description: 'Canceled.' },
    { status: 'ERROR', description: 'Something went wrong. Our support has been notified.' },
  ];

  readonly errorShapeExample = errorExample;
  readonly callbackExample = callbackExample;

  readonly navigation: { title: string, items: IApiDocNavItem[] }[] = [
    {
      title: 'Getting started',
      items: [
        { id: 'introduction', label: 'Introduction' },
        { id: 'authentication', label: 'Authentication' },
        { id: 'errors', label: 'Errors' },
      ]
    },
    {
      title: 'Endpoints',
      items: this.endpoints.map(e => ({ id: e.id, label: e.title, method: e.method }))
    },
    {
      title: 'Guides',
      items: [
        { id: 'print-files', label: 'Print files' },
        { id: 'callbacks', label: 'Callbacks' },
        { id: 'sandbox', label: 'Sandbox orders' },
        { id: 'order-statuses', label: 'Order statuses' },
      ]
    },
    {
      title: 'Data types',
      items: this.types.map(t => ({ id: t.id, label: t.name }))
    }
  ];

  constructor(private elementRef: ElementRef<HTMLElement>) {
  }

  ngAfterViewInit() {
    // Highlights the navigation item of the section at the top of the viewport
    this.observer = new IntersectionObserver(entries => {
      let visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visible.length)
        this.activeSectionId = visible[0].target.id;
    }, { rootMargin: '-15% 0px -75% 0px' });

    this.elementRef.nativeElement
      .querySelectorAll('[data-doc-section]')
      .forEach(section => this.observer?.observe(section));
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    clearTimeout(this.copiedTimeout);
  }

  scrollTo(id: string, event?: Event) {
    event?.preventDefault();
    this.activeSectionId = id;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  json(value: any): string {
    return JSON.stringify(value, null, 2);
  }

  curl(endpoint: IApiDocEndpoint): string {
    let lines = [
      `curl -X ${endpoint.method} "${this.baseUrl}${endpoint.path}${endpoint.exampleQuery ?? ''}"`,
      `  -H "Access-Code: ACCESS_CODE"`,
    ];

    if (endpoint.requestExample) {
      let body = JSON.stringify(endpoint.requestExample, null, 2).split('\n').join('\n  ');
      lines.push(`  -H "Content-Type: application/json"`);
      lines.push(`  -d '${body}'`);
    }

    return lines.join(' \\\n');
  }

  async copy(key: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.copiedKey = key;
      clearTimeout(this.copiedTimeout);
      this.copiedTimeout = setTimeout(() => this.copiedKey = undefined, 1600);
    }
    catch (e) {
      console.log('Failed to copy to clipboard', e);
    }
  }
}
